import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Store, LogOut, ExternalLink, ClipboardList, UtensilsCrossed, Boxes, Bike, CreditCard } from "lucide-react";
import OrdersBoard from "@/components/owner/OrdersBoard";
import MenuManager from "@/components/owner/MenuManager";
import InventoryManager from "@/components/owner/InventoryManager";
import DeliverySettings from "@/components/owner/DeliverySettings";
import PaymentsSettings from "@/components/owner/PaymentsSettings";

export default function OwnerPanel() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [resto, setResto] = useState(null);
  const tab = params.get("tab") || "orders";

  const loadResto = () => api.get("/owner/restaurant").then(({ data }) => setResto(data));
  useEffect(() => { loadResto(); }, []);

  const [toggling, setToggling] = useState(false);
  const toggleOpen = async (next) => {
    setToggling(true);
    setResto((r) => ({ ...r, is_open: next }));
    try {
      await api.put("/owner/status", { is_open: next });
      toast.success(next ? "Restaurante ABIERTO — visible en la app" : "Restaurante CERRADO — la app mostrará 'Cerrado'");
    } catch (e) {
      setResto((r) => ({ ...r, is_open: !next }));
      toast.error(apiErr(e));
    } finally { setToggling(false); }
  };

  const tabs = [
    { v: "orders", label: "Pedidos", icon: ClipboardList },
    { v: "menu", label: "Menú", icon: UtensilsCrossed },
    { v: "inventory", label: "Inventario", icon: Boxes },
    { v: "delivery", label: "Delivery", icon: Bike },
    { v: "payments", label: "Pagos", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-neutral-100 font-adminbody text-neutral-900">
      <header className="sticky top-0 z-20 bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/domifood-logo.png" alt="Domifood" className="h-7 w-auto" />
            <div className="border-l border-neutral-200 pl-3">
              <div className="font-admin font-bold leading-none">{resto?.name || "Mi restaurante"}</div>
              <div className="text-xs text-neutral-500">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {resto && (
              <div className={`flex items-center gap-2.5 rounded-full border px-3 py-1.5 ${resto.is_open ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="open-status-toggle-wrap">
                <span className={`w-2 h-2 rounded-full ${resto.is_open ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                <span className={`text-xs font-semibold ${resto.is_open ? "text-emerald-700" : "text-red-700"}`} data-testid="open-status-label">
                  {resto.is_open ? "Abierto" : "Cerrado"}
                </span>
                <Switch checked={!!resto.is_open} disabled={toggling} onCheckedChange={toggleOpen} data-testid="open-status-toggle" />
              </div>
            )}
            {resto && (
              <a href={`/r/${resto.slug}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="view-store-btn"><ExternalLink className="w-4 h-4 mr-1" /> Ver mi app</Button>
              </a>
            )}
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/login"); }} data-testid="owner-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList className="mb-6 flex flex-wrap h-auto">
            {tabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}>
                <t.icon className="w-4 h-4 mr-1.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="orders"><OrdersBoard /></TabsContent>
          <TabsContent value="menu"><MenuManager /></TabsContent>
          <TabsContent value="inventory"><InventoryManager /></TabsContent>
          <TabsContent value="delivery"><DeliverySettings resto={resto} onSaved={loadResto} /></TabsContent>
          <TabsContent value="payments"><PaymentsSettings resto={resto} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
